import { RADIUS } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { FORMAT_ICONS } from "~/components/news/format-icons";
import { formatLabel } from "~/lib/taxonomy";
import type { ContentFormat } from "~/types/news";

/**
 * The format marker that sits on a card's artwork.
 *
 * A card has to answer "will this play or will I read it" before it is tapped —
 * on a phone, opening the wrong kind of thing costs a page load and a trip back.
 * The badge is the answer, and it carries its own text rather than relying on the
 * icon alone: four formats are two more than an icon vocabulary reliably conveys,
 * and two of them are both video.
 *
 * It sits top-left. The brand's Dots stamp owns the bottom-right of every cover
 * (see `ArticleCover`), and the two must not collide.
 */
export function FormatBadge({ format }: { format: ContentFormat }) {
  const { t } = useTranslation();
  const Icon = FORMAT_ICONS[format];

  return (
    <Box
      data-testid={`news.format-badge-${format}`}
      sx={{
        position: "absolute",
        top: 12,
        insetInlineStart: 12,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        paddingInline: 1,
        paddingBlock: 0.375,
        borderRadius: `${RADIUS.full}px`,
        // Its own scrim rather than a theme surface: the badge sits on artwork the
        // app does not control, so it has to stay legible over a photograph as well
        // as over a gradient, which is what `mediaScrim` is for.
        backgroundColor: "mediaScrim.main",
        backdropFilter: "blur(4px)",
        color: "mediaScrim.contrastText",
        pointerEvents: "none",
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
      <Typography
        variant="L3"
        sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
      >
        {formatLabel(t, format)}
      </Typography>
    </Box>
  );
}
